import { FeatureMaintenanceConfig, SystemConfig } from '@superfunnel/types';
import { onSnapshot } from 'firebase/firestore';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getSystemConfigDocRef } from '../services/SystemConfigService';
import { AuthContext } from './AuthContext';

type MaintenanceKey = 'htmlStepperEditor';

interface SystemConfigContextProps {
  systemConfig: SystemConfig | null;
  loading: boolean;
  /**
   * Check if a specific feature is under maintenance.
   * Automatically returns false for support admins.
   */
  isFeatureUnderMaintenance: (feature: MaintenanceKey) => boolean;
  /**
   * Check if a specific screen/page is under maintenance.
   * Automatically returns false for support admins.
   */
  isScreenUnderMaintenance: (screen: string) => boolean;
  /**
   * Get the maintenance config for a feature (if enabled).
   * Returns undefined if not under maintenance or if user is support admin.
   */
  getFeatureMaintenanceConfig: (
    feature: MaintenanceKey,
  ) => FeatureMaintenanceConfig | undefined;
  /**
   * Get the maintenance config for a screen (if enabled).
   * Returns undefined if not under maintenance or if user is support admin.
   */
  getScreenMaintenanceConfig: (
    screen: string,
  ) => FeatureMaintenanceConfig | undefined;
}

const SystemConfigContext = createContext<SystemConfigContextProps>({
  systemConfig: null,
  loading: true,
  isFeatureUnderMaintenance: () => false,
  isScreenUnderMaintenance: () => false,
  getFeatureMaintenanceConfig: () => undefined,
  getScreenMaintenanceConfig: () => undefined,
});

/**
 * Hook to access system configuration from context.
 * Must be used within SystemConfigProvider.
 */
export function useSystemConfig() {
  return useContext(SystemConfigContext);
}

interface SystemConfigProviderProps {
  children: ReactNode;
}

/**
 * Provider that fetches and subscribes to system configuration from Firestore.
 * Place this near the root of the app to ensure a single subscription.
 */
export function SystemConfigProvider({ children }: SystemConfigProviderProps) {
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const { isSupportAdmin } = useContext(AuthContext);

  useEffect(() => {
    const docRef = getSystemConfigDocRef();

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setSystemConfig(snapshot.data());
        } else {
          setSystemConfig(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching system config:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const isFeatureUnderMaintenance = useCallback(
    (feature: MaintenanceKey): boolean => {
      if (isSupportAdmin) return false;
      return systemConfig?.maintenance?.[feature]?.enabled ?? false;
    },
    [systemConfig, isSupportAdmin],
  );

  const isScreenUnderMaintenance = useCallback(
    (screen: string): boolean => {
      if (isSupportAdmin) return false;
      return systemConfig?.maintenance?.screens?.[screen]?.enabled ?? false;
    },
    [systemConfig, isSupportAdmin],
  );

  const getFeatureMaintenanceConfig = useCallback(
    (feature: MaintenanceKey): FeatureMaintenanceConfig | undefined => {
      if (isSupportAdmin) return undefined;
      const config = systemConfig?.maintenance?.[feature];
      return config?.enabled ? config : undefined;
    },
    [systemConfig, isSupportAdmin],
  );

  const getScreenMaintenanceConfig = useCallback(
    (screen: string): FeatureMaintenanceConfig | undefined => {
      if (isSupportAdmin) return undefined;
      const config = systemConfig?.maintenance?.screens?.[screen];
      return config?.enabled ? config : undefined;
    },
    [systemConfig, isSupportAdmin],
  );

  return (
    <SystemConfigContext.Provider
      value={{
        systemConfig,
        loading,
        isFeatureUnderMaintenance,
        isScreenUnderMaintenance,
        getFeatureMaintenanceConfig,
        getScreenMaintenanceConfig,
      }}
    >
      {children}
    </SystemConfigContext.Provider>
  );
}
